import React from 'react';
import { ConnectTokenResponse } from '@esg/business-account';
import { getEnv } from '@esg/shared';
import { useLoginState } from '@esg/framework';
import { useLoginStore } from '@esg/ui/hooks/useExistingLogin';
import { convertOpenIdResponse } from '../utils';

interface ExchangeTokenPeriodicallyPayload {
    readonly getRefreshToken: () => string | null | undefined;
    readonly setTokens: (tokens: ConnectTokenResponse) => void;
    readonly interval: number;
}

export function useExchangeToken() {

    const [, setLoginState] = useLoginState();
    const {
        setData: saveLogin,
    } = useLoginStore();

    const intervalRef = React.useRef<NodeJS.Timeout | null>(null);

    const exchangeToken = React.useCallback(async (refreshToken: string): Promise<ConnectTokenResponse> => {
        const identityServer = getEnv().IDENTITY_SERVER;
        const clientId = getEnv().MANAGEMENT_CLIENT_ID;

        const formBody = new URLSearchParams();
        formBody.append('grant_type', 'refresh_token');
        formBody.append('refresh_token', refreshToken);
        formBody.append('client_id', clientId);

        const response = await fetch(`${identityServer}/connect/token`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded',
            },
            body: formBody.toString(),
        });

        if (!response.ok) {
            throw new Error('Failed to exchange token');
        }

        const data = await response.json();
        return data;
    }, []);

    const exchangeTokenPeriodically = React.useCallback((options: ExchangeTokenPeriodicallyPayload) => {
        const interval = options.interval;
        intervalRef.current = setInterval(async () => {
            const refreshToken = options.getRefreshToken();
            if (!refreshToken) {
                return null;
            }
            const response = await exchangeToken(refreshToken);
            // console.log('exchangeToken:response', response);
            try {
                const loginStateValue = convertOpenIdResponse(response);
                saveLogin(loginStateValue);
                setLoginState(loginStateValue);
            }
            catch {
                console.log('refresh error');
            }

        }, interval);

        return intervalRef.current;
    }, [exchangeToken, saveLogin, setLoginState]);

    React.useEffect(() => {
        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
            }
        };
    }, []);

    return {
        exchangeToken,
        exchangeTokenPeriodically,
    };
}